'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { SlideForm, type SlideFormRef } from '@/components/partials/slides'
import { ContentError, FormSkeleton } from '@/components/partials'
import { type CreateSlideParams } from '@/types/slide'
import { useSlide, useUpdateSlide } from '@/hooks'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const slideFormRef = useRef<SlideFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: slide, isLoading, error } = useSlide(id)
  const { mutate: updateSlide, isPending } = useUpdateSlide()

  const handleSubmit = (data: CreateSlideParams) => {
    updateSlide(
      { id, params: data },
      {
        onSuccess: async updatedSlide => {
          setIsRedirecting(true)
          queryClient.setQueryData(['slide', id], updatedSlide)
          await queryClient.invalidateQueries({ queryKey: ['slides'] })
          toast.success(t('updated_successfully'))
          router.push(`/galleries/slides/${updatedSlide.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (slideFormRef.current) {
        slideFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/galleries/slides/${id}`)
  }

  if (isLoading || !slide) {
    return <FormSkeleton fieldRows={1} showPictures />
  }

  if (error || !slide) {
    return <ContentError error={error} listUrl="/galleries/slides" />
  }

  return (
    <SlideForm
      ref={slideFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={slide}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
